import { useState } from 'react'
import type { DbCoupleInvite } from '../../types'
import { getToastErrorMessage, useToast } from '../Toast'

interface InvitePartnerSectionProps {
  pendingInvite: DbCoupleInvite | null
  partnerUserId: string | null
  memberCount: number
  loading: boolean
  hookError: string | null
  onUnlinked: (newHouseholdId: string) => void
  sendInvite: (email: string) => Promise<void>
  cancelInvite: () => Promise<void>
  resendInvite: (email: string) => Promise<void>
  unlinkCouple: () => Promise<string>
}

export default function InvitePartnerSection({
  pendingInvite,
  partnerUserId,
  memberCount,
  loading,
  hookError,
  onUnlinked,
  sendInvite,
  cancelInvite,
  resendInvite,
  unlinkCouple,
}: InvitePartnerSectionProps) {
  const toast = useToast()
  const [emailInput,    setEmailInput]    = useState('')
  const [sending,       setSending]       = useState(false)
  const [sendError,     setSendError]     = useState<string | null>(null)
  const [sendSuccess,   setSendSuccess]   = useState(false)
  const [resending,     setResending]     = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)
  const [unlinkStep,    setUnlinkStep]    = useState<0 | 1 | 2>(0)
  const [unlinking,     setUnlinking]     = useState(false)
  const [unlinkError,   setUnlinkError]   = useState<string | null>(null)

  const hasPartner = memberCount >= 2

  async function handleSend(e: React.FormEvent) {
    e.preventDefault()
    if (!emailInput.trim()) return
    setSending(true)
    setSendError(null)
    setSendSuccess(false)
    try {
      await sendInvite(emailInput.trim())
      setSendSuccess(true)
      setEmailInput('')
      toast.success('Convite enviado.')
    } catch (err) {
      const message = getToastErrorMessage(err, 'Erro ao enviar convite.')
      setSendError(message)
      toast.error(message)
    } finally {
      setSending(false)
    }
  }

  async function handleCancelInvite() {
    try {
      await cancelInvite()
      toast.success('Convite cancelado.')
    } catch (err) {
      const message = getToastErrorMessage(err, 'Erro ao cancelar convite.')
      setSendError(message)
      toast.error(message)
    }
  }

  async function handleResend() {
    if (!pendingInvite) return
    setResending(true)
    setResendSuccess(false)
    setSendError(null)
    try {
      await resendInvite(pendingInvite.invitee_email)
      setResendSuccess(true)
      toast.success('Convite reenviado.')
    } catch (err) {
      const message = getToastErrorMessage(err, 'Erro ao reenviar convite.')
      setSendError(message)
      toast.error(message)
    } finally {
      setResending(false)
    }
  }

  async function handleUnlink() {
    setUnlinking(true)
    setUnlinkError(null)
    try {
      const newId = await unlinkCouple()
      onUnlinked(newId)
      toast.success('Parceiro removido.')
    } catch (err) {
      const message = getToastErrorMessage(err, 'Erro ao remover parceiro.')
      setUnlinkError(message)
      toast.error(message)
      setUnlinking(false)
      setUnlinkStep(1)
    }
  }

  function daysUntil(isoDate: string): number {
    const diff = new Date(isoDate).getTime() - Date.now()
    return Math.max(0, Math.ceil(diff / 86_400_000))
  }

  if (loading) return null

  return (
    <div className="bc-account-settings__card">
      <div className="bc-account-settings__item-title">Conta Casal</div>

      {/* Estado 3: Parceiro vinculado */}
      {hasPartner && partnerUserId && (
        <>
          <div className="bc-account-settings__item-desc" style={{ marginTop: '0.5rem' }}>
            Vocês estão conectados e compartilhando dados em tempo real.
          </div>

          {unlinkStep === 0 && (
            <div style={{ marginTop: '1rem' }}>
              <button
                className="bc-btn bc-btn--secondary"
                onClick={() => setUnlinkStep(1)}
              >
                Remover parceiro
              </button>
            </div>
          )}

          {unlinkStep === 1 && (
            <div className="bc-account-settings__confirm">
              <p className="bc-account-settings__confirm-text">
                Ao desvincular, suas transações serão separadas. Cada um volta a ter uma conta
                individual. Esta ação <strong>não pode ser desfeita</strong>.
              </p>
              {unlinkError && <p className="bc-account-settings__error">{unlinkError}</p>}
              <div className="bc-account-settings__confirm-actions">
                <button
                  className="bc-btn bc-btn--secondary"
                  onClick={() => { setUnlinkStep(0); setUnlinkError(null) }}
                  disabled={unlinking}
                >
                  Cancelar
                </button>
                <button
                  className="bc-btn bc-btn--danger"
                  onClick={() => setUnlinkStep(2)}
                  disabled={unlinking}
                >
                  Sim, desvincular
                </button>
              </div>
            </div>
          )}

          {unlinkStep === 2 && (
            <div className="bc-account-settings__confirm">
              <p className="bc-account-settings__confirm-text">
                Última confirmação: você perderá acesso às transações criadas pelo seu parceiro.
              </p>
              {unlinkError && <p className="bc-account-settings__error">{unlinkError}</p>}
              <div className="bc-account-settings__confirm-actions">
                <button
                  className="bc-btn bc-btn--secondary"
                  onClick={() => { setUnlinkStep(0); setUnlinkError(null) }}
                  disabled={unlinking}
                >
                  Cancelar
                </button>
                <button
                  className="bc-btn bc-btn--danger"
                  onClick={handleUnlink}
                  disabled={unlinking}
                >
                  {unlinking ? 'Desvinculando…' : 'Confirmar desvinculação'}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Estado 2: Convite pendente */}
      {!hasPartner && pendingInvite && (
        <div style={{ marginTop: '0.75rem' }}>
          <div className="bc-account-settings__item-desc">
            Convite enviado para <strong>{pendingInvite.invitee_email}</strong>.
            Expira em {daysUntil(pendingInvite.expires_at)} dia(s).
          </div>
          {resendSuccess && (
            <p style={{ margin: '0.5rem 0 0', fontSize: 'var(--fs-small)', color: 'var(--income-600)' }}>
              Convite reenviado com sucesso!
            </p>
          )}
          {sendError && (
            <p className="bc-account-settings__error" style={{ marginTop: '0.5rem' }}>
              {sendError}
            </p>
          )}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
            <button
              className="bc-btn bc-btn--primary"
              onClick={handleResend}
              disabled={resending}
            >
              {resending && (
                <svg className="bc-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                </svg>
              )}
              {resending ? 'Reenviando…' : 'Reenviar convite'}
            </button>
            <button
              className="bc-btn bc-btn--secondary"
              onClick={handleCancelInvite}
              disabled={resending}
            >
              Cancelar convite
            </button>
          </div>
        </div>
      )}

      {/* Estado 1: Nenhum parceiro, sem convite */}
      {!hasPartner && !pendingInvite && (
        <>
          <div className="bc-account-settings__item-desc" style={{ marginTop: '0.5rem' }}>
            Convide seu parceiro para compartilhar finanças em tempo real.
          </div>

          {sendSuccess && (
            <p style={{ margin: '0.75rem 0 0', fontSize: 'var(--fs-small)', color: 'var(--income-600)' }}>
              Convite enviado com sucesso!
            </p>
          )}

          {(sendError || hookError) && (
            <p className="bc-account-settings__error" style={{ marginTop: '0.5rem' }}>
              {sendError ?? hookError}
            </p>
          )}

          <form onSubmit={handleSend} style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
            <input
              type="email"
              required
              className="bc-input"
              placeholder="email@parceiro.com"
              value={emailInput}
              onChange={e => { setEmailInput(e.target.value); setSendError(null); setSendSuccess(false) }}
              style={{ flex: 1, minWidth: '200px' }}
            />
            <button
              type="submit"
              className="bc-btn bc-btn--primary"
              disabled={sending || !emailInput.trim()}
            >
              {sending ? 'Enviando…' : 'Convidar parceiro'}
            </button>
          </form>
        </>
      )}
    </div>
  )
}
